import React from 'react';
import { createContext } from 'react';

import type { ModalType } from '../types';

// Context 생성
export const ModalContext = createContext<{
  size: ModalType['size'];
  color: ModalType['color'];
}>({
  size: 'md', // 기본값
  color: 'white', // 기본값
});

type Props = {
  size: ModalType['size'];
  color?: ModalType['color'];
  isHeightAuto?: boolean;
  className?: string;
  children: React.ReactNode;
};

const COLOR_CLASS: Record<'white' | 'black', string> = {
  white: 'bg-apps-coreGrayWhite',
  black: 'bg-apps-coreGrayBlack',
};

/** xs/sm/md/lg 는 max-width 만 다르고 height 규칙이 같다 */
const MAX_WIDTH_CLASS: Record<string, string> = {
  xs: 'max-w-[335px]',
  sm: 'max-w-[450px]',
  md: 'max-w-[640px]',
  lg: 'max-w-[900px]',
};

/*
 * full 은 md(768px) 이하에서 전체 화면이 된다.
 * 원본은 이 분기에서 customScrollStyle 을 걸었지만 인자가 무효 색상이라
 * 크기/반경만 적용됐다 (globals.css 의 .custom-scroll-modal 주석 참고).
 */
const FULL_CLASS =
  'w-[calc(100%-160px)] max-h-full [@media(max-width:768px)]:h-full [@media(max-width:768px)]:w-full [@media(max-width:768px)]:max-w-full [@media(max-width:768px)]:translate-x-0 [@media(max-width:768px)]:translate-y-0 [@media(max-width:768px)]:rounded-none custom-scroll-modal-max-md';

export default function Modal({
  size,
  color = 'white',
  isHeightAuto = false,
  className,
  children,
}: Props) {
  // 원본에는 auto 분기도 있었으나 ModalType['size'] 에 'auto' 가 없어 도달 불가였다.
  // size 가 undefined 면 원본의 `size && ...` 처럼 크기 클래스를 붙이지 않는다.
  const sizeClass = !size
    ? ''
    : size === 'full'
      ? FULL_CLASS
      : `${MAX_WIDTH_CLASS[size] ?? ''} ${
          isHeightAuto ? 'h-auto' : 'h-[calc(100%-40px)]'
        }`;

  return (
    <ModalContext.Provider value={{ size, color }}>
      <div
        className={`relative z-[800] mx-auto flex min-h-[226px] max-h-[680px] w-[calc(100%-40px)] flex-col justify-stretch overflow-hidden rounded-xl ${
          COLOR_CLASS[color ?? 'white']
        } ${sizeClass} ${className ?? ''}`}
      >
        {children}
      </div>
    </ModalContext.Provider>
  );
}
